import { FormEvent, useState } from 'react'

import * as api from 'api/api'
import { Issue, IssueSeverity } from 'api/types'
import { useConfirm } from 'components/Confirm'
import { useProject } from 'components/Layout'
import { Button, Card, EmptyState, ErrorState, Modal, SelectField, Spinner, TextField } from 'components/ui'
import { formatDateTime } from 'lib/format'
import { useAction, useAsync } from 'lib/hooks'
import { t } from 'lib/i18n'

const severityLabel = (severity: IssueSeverity) => {
  if (severity === 'HIGH') return t('عالية', 'High')
  if (severity === 'LOW') return t('منخفضة', 'Low')
  return t('متوسطة', 'Medium')
}
const SEVERITY_BADGE: Record<IssueSeverity, string> = { HIGH: 'badge badge--missing', LOW: 'badge badge--new', MEDIUM: 'badge badge--draft' }

const ReportIssueSheet = ({ onClose, onCreated }: { onClose: () => void; onCreated: (issue: Issue) => void }) => {
  const { project } = useProject()
  const { busy, run } = useAction()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [severity, setSeverity] = useState<IssueSeverity>('MEDIUM')

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    const created = await run('create', () => api.createIssue(project.id, { description: description.trim() || null, severity, title: title.trim() }))
    if (created) onCreated(created)
  }

  return (
    <Modal title={t('الإبلاغ عن مشكلة', 'Report an issue')} onClose={onClose}>
      <form className="form-grid" onSubmit={submit}>
        <TextField label={t('عنوان المشكلة', 'Issue title')} value={title} onChange={(event) => setTitle(event.target.value)} required className="span-2" />
        <SelectField label={t('درجة الخطورة', 'Severity')} value={severity} onChange={(event) => setSeverity(event.target.value as IssueSeverity)}>
          {(['HIGH', 'LOW', 'MEDIUM'] as IssueSeverity[]).map((key) => (
            <option key={key} value={key}>
              {severityLabel(key)}
            </option>
          ))}
        </SelectField>
        <TextField label={t('تفاصيل (اختياري)', 'Details (optional)')} value={description} onChange={(event) => setDescription(event.target.value)} className="span-2" />
        <div className="form-actions span-2">
          <Button onClick={onClose}>{t('إلغاء', 'Cancel')}</Button>
          <Button type="submit" variant="primary" busy={busy === 'create'} disabled={!title.trim()}>
            {t('إبلاغ', 'Report')}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

/** Project issue tracker (Figma: Admin / 03 · Site detail — Issues) — any project member can report a
 *  problem; resolving/reopening/deleting is manager-only, same gate as site settings. */
export const IssuesPage = () => {
  const { project, isManager } = useProject()
  const { data, error, loading, reload } = useAsync(() => api.fetchIssues(project.id), [project.id])
  const { busy, run } = useAction()
  const confirm = useConfirm()
  const [sheetOpen, setSheetOpen] = useState(false)

  const open = data?.filter((issue) => issue.status === 'OPEN') ?? []
  const resolved = data?.filter((issue) => issue.status === 'RESOLVED') ?? []

  const onResolve = (id: string) => run(id, () => api.resolveIssue(id), t('تم حل المشكلة', 'Issue resolved')).then((done) => done && reload())
  const onReopen = (id: string) => run(id, () => api.reopenIssue(id), t('تمت إعادة فتح المشكلة', 'Issue reopened')).then((done) => done && reload())
  const onRemove = async (id: string, title: string) => {
    const sure = await confirm(t(`حذف المشكلة «${title}»؟`, `Delete the issue "${title}"?`), { tone: 'danger' })
    if (!sure) return
    const done = await run(id, () => api.removeIssue(id), t('تم حذف المشكلة', 'Issue deleted'))
    if (done) reload()
  }

  const IssueRow = ({ issue }: { issue: Issue }) => (
    <li className="today-recent__row">
      <span className={SEVERITY_BADGE[issue.severity]}>{severityLabel(issue.severity)}</span>
      <span className="today-recent__body">
        <strong>{issue.title}</strong>
        <span className="muted small">
          {issue.reportedByName} · {formatDateTime(issue.createdAt)}
          {issue.status === 'RESOLVED' && issue.resolvedByName ? t(` · حُلّت بواسطة ${issue.resolvedByName}`, ` · Resolved by ${issue.resolvedByName}`) : ''}
        </span>
        {issue.description ? <span className="muted small">{issue.description}</span> : null}
      </span>
      {isManager ? (
        <div className="form-actions">
          {issue.status === 'OPEN' ? (
            <Button size="sm" variant="primary" icon="check" busy={busy === issue.id} onClick={() => onResolve(issue.id)}>
              {t('حل', 'Resolve')}
            </Button>
          ) : (
            <Button size="sm" icon="undo" busy={busy === issue.id} onClick={() => onReopen(issue.id)}>
              {t('إعادة فتح', 'Reopen')}
            </Button>
          )}
          <Button size="sm" variant="ghost" icon="trash" busy={busy === issue.id} onClick={() => onRemove(issue.id, issue.title)}>
            {t('حذف', 'Delete')}
          </Button>
        </div>
      ) : null}
    </li>
  )

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>{t('المشاكل', 'Issues')}</h1>
          <p className="muted">{data ? t(`${open.length} مفتوحة · ${resolved.length} مُغلقة`, `${open.length} open · ${resolved.length} closed`) : t('مشاكل وملاحظات تخص الموقع', 'Issues and notes for this site')}</p>
        </div>
        <Button variant="primary" icon="plus" onClick={() => setSheetOpen(true)}>
          {t('الإبلاغ عن مشكلة', 'Report an issue')}
        </Button>
      </div>

      {loading && !data ? (
        <Spinner />
      ) : error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : !data?.length ? (
        <EmptyState icon="alert" title={t('لا توجد مشاكل مُسجّلة', 'No issues recorded')} />
      ) : (
        <>
          <Card title={t(`مفتوحة (${open.length})`, `Open (${open.length})`)}>
            {open.length ? (
              <ul className="today-recent">
                {open.map((issue) => (
                  <IssueRow issue={issue} key={issue.id} />
                ))}
              </ul>
            ) : (
              <p className="muted small">{t('لا توجد مشاكل مفتوحة حالياً', 'No open issues right now')}</p>
            )}
          </Card>
          {resolved.length ? (
            <Card title={t(`مُغلقة (${resolved.length})`, `Closed (${resolved.length})`)}>
              <ul className="today-recent">
                {resolved.map((issue) => (
                  <IssueRow issue={issue} key={issue.id} />
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}

      {sheetOpen ? (
        <ReportIssueSheet
          onClose={() => setSheetOpen(false)}
          onCreated={() => {
            setSheetOpen(false)
            reload()
          }}
        />
      ) : null}
    </div>
  )
}
